/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createBrowserHistory } from 'history';
import urlJoin, { Options } from 'proper-url-join';

import { getPath, getServerBaseUri, normalizePath } from './url';

/**
 * Create a custom history object to use with react-router.
 * We do this so that it is possible to use the history wrapper
 * api outside of React components wrapped by Router.
 */
const history = createBrowserHistory();

/**
 * When the history is initialized we check that the current path is normalized
 * as it would have been in the Angular version of the app.
 * If it has not, normalize it and push that to the location.
 * In angular-ui-router, paths are normalized whenever they are set/get using the
 * $location service, and the $URLRouterProvider does this for the initial URL.
 * In the .next version of this app, subsequent transitions are handled by history
 * and react-router using their API, so we only need this once.
 */

const normalizedPath = normalizePath(history.location.pathname);

if (normalizedPath !== history.location.pathname) {
  history.replace(normalizedPath);
}

/**
 * Perform a logout by redirecting the user to the logout endpoint on the
 * server, which clears any session information before redirecting the user
 * to the IAM logout endpoint.
 */
export const doLogout = (): void => {
  setTimeout(() => {
    window.location.href = urlJoin(getServerBaseUri(), 'logout', {
      query: {
        redirect: window.location.pathname
      }
    });
  }, 300);
};

/**
 * Perform a login by redirecting the user to the login endpoint on the
 * server, passing a query parameter that will redirect the user to the
 * page the user is currently on.
 *
 * The login component will use this "redirect" query parameter and specify
 * it as the URL used upon a successful login.
 *
 * @param instanceId If present, will prepend the provided instance ID before
 * the login path.
 */
export const doLogin = (instanceId?: string): void => {
  // The query parameters should be either the current ones, if any, or
  // we will add a "redirect" query parameter containing the current
  // page's path.
  const options: Options = {
    query: window.location.search
      ? Object.fromEntries(new URLSearchParams(window.location.search))
      : {
          redirect: window.location.pathname
        }
  };

  const basename = getPath(document.baseURI);

  const loginRedirect = urlJoin(
    instanceId ? encodeURIComponent(instanceId) : '',
    basename ?? '',
    'login',
    options
  );

  history.push(loginRedirect);
};

export default history;
